// src/features/map/layers/AircraftPanel.jsx
// Premium flight detail panel — end-side glass drawer (Trackizy design, not an
// FR24 clone). Opens on plane click when the org has featureAircraftLayerPremium;
// fetches the bundled track+summary endpoint and drops the trail on the map.
// Rendered from AircraftOverlay, which portals its whole output to
// document.body — so this panel positions with `fixed` (not `absolute`,
// which would resolve against body with no containing block) and relies on
// the caller to wrap it in the same createPortal.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plane, X, ArrowRight, Clock } from 'lucide-react';
import { layers as layersApi } from '@/lib/trackizy';
import { getAirport, flightProgress } from './airportCoords';

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function AircraftPanel({ flight, mapViewRef, onClose }) {
  const { t } = useTranslation();
  const [track, setTrack] = useState(null); // {geometry, points, summary}

  // Fetch trail + summary; push trail to the map; clear on close/flight change.
  useEffect(() => {
    if (!flight?.fr24Id) return undefined;
    let stale = false;
    setTrack(null);
    layersApi.track(flight.fr24Id)
      .then((res) => {
        if (stale) return;
        setTrack(res);
        if (res?.geometry?.coordinates?.length) {
          mapViewRef.current?.setAircraftTrail?.({
            type: 'FeatureCollection',
            features: [{ type: 'Feature', geometry: res.geometry, properties: {} }],
          });
        }
      })
      .catch(() => { /* panel renders without trail/summary */ });
    return () => {
      stale = true;
      mapViewRef.current?.setAircraftTrail?.(null);
    };
  }, [flight?.fr24Id, mapViewRef]);

  if (!flight) return null;
  const orig = getAirport(flight.origIata);
  const dest = getAirport(flight.destIata);
  const progress = flight.origIata && flight.destIata
    ? flightProgress({
        origIata: flight.origIata, destIata: flight.destIata,
        lat: flight.__lat, lon: flight.__lon, flownKm: track?.summary?.flownKm ?? null,
      })
    : null;

  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-3 text-xs py-1">
      <span className="text-slate-400">{label}</span>
      <span className="text-slate-700 dark:text-slate-200 font-medium"><bdi>{value ?? '—'}</bdi></span>
    </div>
  );

  return (
    <div
      className="fixed top-14 bottom-4 end-3 w-[300px] z-40 rounded-2xl shadow-xl overflow-y-auto p-4"
      style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--glass-border)' }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Plane size={16} className="text-sky-500" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
            <bdi>{flight.flightNo || flight.callsign || flight.icao}</bdi>
          </div>
          {flight.airline && <div className="text-[10px] text-slate-400"><bdi>{flight.airline}</bdi></div>}
        </div>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={14} /></button>
      </div>

      {(flight.origIata || flight.destIata) && (
        <div className="rounded-xl bg-slate-100/60 dark:bg-slate-800/60 p-3 mb-3">
          <div className="flex items-center justify-between text-center">
            <div className="flex-1">
              <div className="text-lg font-bold text-slate-800 dark:text-slate-100"><bdi>{flight.origIata || '—'}</bdi></div>
              <div className="text-[9px] text-slate-400 truncate"><bdi>{orig?.name || ''}</bdi></div>
            </div>
            <ArrowRight size={14} className="text-slate-400 shrink-0 rtl:rotate-180" />
            <div className="flex-1">
              <div className="text-lg font-bold text-slate-800 dark:text-slate-100"><bdi>{flight.destIata || '—'}</bdi></div>
              <div className="text-[9px] text-slate-400 truncate"><bdi>{dest?.name || ''}</bdi></div>
            </div>
          </div>
          {progress && (
            <>
              <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 mt-2 overflow-hidden">
                <div className="h-full rounded-full bg-sky-500" style={{ width: `${progress.pct}%` }} />
              </div>
              <div className="flex justify-between text-[9px] text-slate-400 mt-1">
                <span><bdi>{progress.flownKm.toLocaleString()} km</bdi></span>
                <span><bdi>{progress.remainingKm.toLocaleString()} km</bdi></span>
              </div>
            </>
          )}
          <div className="flex justify-between text-[10px] mt-2">
            <span className="text-slate-500 flex items-center gap-1"><Clock size={10} />{t('mapLayers.departed', 'Departed')} <bdi>{fmtTime(track?.summary?.departedAt)}</bdi></span>
            <span className="text-slate-500">{t('mapLayers.eta', 'ETA')} <bdi>{fmtTime(flight.eta)}</bdi></span>
          </div>
        </div>
      )}

      <Row label={t('mapLayers.callsign', 'Callsign')} value={flight.callsign || null} />
      <Row label={t('mapLayers.aircraftType', 'Aircraft')} value={flight.typeCode || null} />
      <Row label={t('mapLayers.registration', 'Registration')} value={flight.reg || null} />
      <Row label={t('mapLayers.altitude', 'Altitude')} value={flight.altFt != null ? `${Number(flight.altFt).toLocaleString()} ft` : null} />
      <Row label={t('mapLayers.speed', 'Speed')} value={flight.speedKt != null ? `${flight.speedKt} kt` : null} />
      <Row label={t('mapLayers.verticalSpeed', 'Vertical speed')} value={flight.vspeedFpm != null ? `${flight.vspeedFpm} fpm` : null} />
      <Row label={t('mapLayers.squawk', 'Squawk')} value={flight.squawk || null} />
      <Row label={t('mapLayers.dataSource', 'Source')} value={flight.source || null} />
    </div>
  );
}
